import { createPortal } from 'react-dom';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Stack from '@mui/material/Stack';
import { AlertTitle } from 'common/Snackbar/AlertTitle';
import { ContentCopyExtensionProps } from './ContentCopyExtension.interface';
import { AutoHideSnackbar } from 'src/common/Snackbar/AutoHideSnackbar';
import { pxToRem } from '../utils/fonts';

const copyWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
};

export const ContentCopyExtension: React.FC<ContentCopyExtensionProps> = ({ children, value }) => {
  const [mouseOver, setMouseOver] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { t } = useTranslation();

  const onMouseOver = useCallback(() => {
    setMouseOver(true);
  }, []);

  const onMouseOut = useCallback(() => {
    setMouseOver(false);
  }, []);

  const onCopyClick = useCallback(
    (event: any) => {
      event.stopPropagation();
      navigator.clipboard.writeText(value);

      setSnackbarOpen(true);
    },
    [value],
  );

  return (
    <>
      <Box
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        data-testid="copy wrapper"
        sx={copyWrapperSx}
      >
        {children}
        {value === '' ? null : (
          <IconButton
            data-testid="Copy value"
            aria-label="Copy value"
            onClick={onCopyClick}
            sx={{
              p: 0,
              px: 4,
              opacity: mouseOver ? 1 : 0,
            }}
          >
            <ContentCopyIcon
              sx={{ fontSize: pxToRem(14), lineHeight: 1.5 }}
              data-testid="ContentCopyIcon"
            />
          </IconButton>
        )}
      </Box>
      {snackbarOpen &&
        createPortal(
          <Stack sx={{ position: 'absolute', top: 0, left: 0 }}>
            <AutoHideSnackbar
              severity="info"
              open={snackbarOpen}
              onClose={() => {
                setSnackbarOpen(false);
              }}
              icon={<ContentCopyIcon data-testid="ContentCopyIcon" />}
            >
              <AlertTitle>{t('common.copiedToClipBoard')}</AlertTitle>
            </AutoHideSnackbar>
          </Stack>,
          document.body,
        )}
    </>
  );
};
